﻿// singleton module to ensure only one msalInstance
import { AccountInfo, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./msalConfig";
import { loginRequest } from "./msalConfig";


declare global {
    interface Window { __msalInstance?: PublicClientApplication }
}

// window.__msalInstance check ensures only one instance exists across hot reloads
export const msalInstance = window.__msalInstance ??
    (window.__msalInstance = new PublicClientApplication(msalConfig));

/*
 * Initialize MSAL and complete any pending redirect
 * Call this ONCE at app startup (index.ts)
 */
export async function bootstrapMsal() {
    try {
        await msalInstance.initialize();                 // Required in v3
        await msalInstance.handleRedirectPromise();      // complete any pending redirect
    } catch (err) {
        //console.error("MSAL bootstrap error:", err);
        throw err;
    }
}


/**
 * Get an access token for the API scopes using the signed-in account.
 * Silent first; if interaction is required, triggers redirect.
 * Reminder - tokens are per account and per scope; do not store as property 
 */
export async function getAccessToken(): Promise<string> {
    const accounts = msalInstance.getAllAccounts();
    const account: AccountInfo | undefined = accounts[0];

    if (!account) {
        // If routes are protected, you shouldn't normally hit this
        // If you reach here, route protection may be misconfigured; in practice
        // MsalAuthenticationTemplate ensures you're signed in already.
        await msalInstance.loginRedirect(loginRequest);
        return ""; // Redirect occurs; return value is not used.
    }

    try {
        // Get from cache first if still valid, otherwise get a fresh, scoped token      
        const tokenResp = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
        });
        return tokenResp.accessToken;
    } catch (err) {
        // If silent fails (consent/MFA/CA), fall back to redirect interaction
        if (err instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect(loginRequest);
            return ""; // Redirect occurs
        }
        throw err;
    }
}

